import { registerPushToken } from '../api/notifications';
import { getFCMToken, isPushSupported, requestNotificationPermission } from './firebase-messaging';

let inFlight: Promise<boolean> | null = null;
let backgroundRetry: Promise<void> | null = null;

function getVapidKey(): string | undefined {
  const key = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
  return key?.trim() || undefined;
}

function currentPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/**
 * Um único registro FCM por vez na aba. Cliques duplicados e o auto-registro
 * do layout compartilham a mesma promise.
 */
export function registerPushOnce(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    if (!isPushSupported()) return false;
    const vapidKey = getVapidKey();
    if (!vapidKey) {
      console.warn(
        'Push: VITE_FIREBASE_VAPID_KEY não configurada. Configure no Firebase Console > Cloud Messaging.',
      );
      return false;
    }

    if (currentPermission() !== 'granted') {
      const result = await requestNotificationPermission();
      if (result !== 'granted') return false;
    }

    const fcmToken = await getFCMToken(vapidKey);
    if (!fcmToken) return false;

    await registerPushToken(fcmToken);
    return true;
  })()
    .catch((error) => {
      console.warn('Push: falha ao registrar', error);
      return false;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Se o iPhone já permitiu avisos, tenta gravar o token sem mostrar erro de novo. */
export function retryRegisterPushInBackground(): void {
  if (backgroundRetry) return;
  backgroundRetry = (async () => {
    for (let attempt = 1; attempt <= 5; attempt += 1) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, 800 * attempt);
      });
      if (currentPermission() !== 'granted') return;
      if (await registerPushOnce()) return;
    }
  })().finally(() => {
    backgroundRetry = null;
  });
}

export function resetPushRegisterForTests(): void {
  inFlight = null;
  backgroundRetry = null;
}
